import React, { useState } from 'react';
import {
  ObjFaqItem,
  CallInsightsAnalysis,
  getScoreBadgeClasses,
  getAdherenceLabelClasses,
  formatAdherenceLabel,
  formatCallInsightsTimestamp,
} from './types';
import SentimentRow from './SentimentRow';
import TranscriptBlock from './TranscriptBlock';
import ManagerReviewPanel from './ManagerReviewPanel';

interface ObjFaqAdherenceSectionProps {
  items: ObjFaqItem[];
  overall_summary: string;
  sessionId: string;
  managerReview?: CallInsightsAnalysis['manager_review'];
  isAdmin: boolean;
  onReviewSaved: () => void;
  onNavigateToTimestamp?: (ts: string) => void;
}

interface ExpandState {
  answers: Set<number>;
  transcripts: Set<number>;
}

const ObjFaqAdherenceSection: React.FC<ObjFaqAdherenceSectionProps> = ({
  items,
  overall_summary,
  sessionId,
  managerReview,
  isAdmin,
  onReviewSaved,
  onNavigateToTimestamp,
}) => {
  const [expandedCard, setExpandedCard] = useState<number | null>(null);
  const [expanded, setExpanded] = useState<ExpandState>({
    answers: new Set(),
    transcripts: new Set(),
  });

  const toggleCard = (idx: number) => {
    setExpandedCard((prev) => (prev === idx ? null : idx));
  };

  const toggleAnswers = (idx: number) => {
    setExpanded((prev) => {
      const next = new Set(prev.answers);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return { ...prev, answers: next };
    });
  };

  const toggleTranscripts = (idx: number) => {
    setExpanded((prev) => {
      const next = new Set(prev.transcripts);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return { ...prev, transcripts: next };
    });
  };

  if (!items || items.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        No objections or FAQs were detected in this session.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {overall_summary && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
          <span className="font-semibold">Overall Summary: </span>
          {overall_summary}
        </div>
      )}

      {items.map((item, idx) => {
        const isOpen = expandedCard === idx;

        return (
          <div
            key={idx}
            className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden"
          >
            {/* Clickable header — always visible */}
            <button
              type="button"
              onClick={() => toggleCard(idx)}
              className="w-full flex flex-wrap items-start justify-between gap-2 p-4 text-left hover:bg-gray-50 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 text-sm leading-snug">
                  {item.question}
                </p>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                    {item.category}
                  </span>
                  {item.timestamp && (
                    <span className="text-xs text-gray-400">{formatCallInsightsTimestamp(item.timestamp)}</span>
                  )}
                  {item.timestamp_iso && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onNavigateToTimestamp?.(item.timestamp_iso!); }}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium underline"
                    >
                      View in Timeline &rarr;
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span
                  className={`text-sm font-bold px-2.5 py-1 rounded ${getScoreBadgeClasses(item.handling_score)}`}
                >
                  {item.handling_score}/100
                </span>
                <span
                  className={`text-xs font-semibold px-2.5 py-1 rounded ${getAdherenceLabelClasses(item.adherence_label)}`}
                >
                  {formatAdherenceLabel(item.adherence_label)}
                </span>
                <span className="text-gray-400 text-xs ml-1">{isOpen ? '▲' : '▼'}</span>
              </div>
            </button>

            {/* Collapsible body */}
            {isOpen && (
              <div className="p-4 border-t border-gray-100 space-y-3">
                {item.adherence_summary && (
                  <p className="text-sm text-gray-700">{item.adherence_summary}</p>
                )}

                {(item.what_worked || item.what_missed || item.what_rep_should_have_said) && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {item.what_worked && (
                      <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded p-2.5 text-sm">
                        <span className="text-green-600 font-bold flex-shrink-0 mt-0.5">&#10003;</span>
                        <p className="text-green-900">{item.what_worked}</p>
                      </div>
                    )}
                    {(item.what_missed || item.what_rep_should_have_said) && (
                      <div className="bg-red-50 border border-red-200 rounded p-2.5 text-sm space-y-2">
                        {item.what_missed && (
                          <div className="flex items-start gap-2">
                            <span className="text-red-600 font-bold flex-shrink-0 mt-0.5">&#10007;</span>
                            <p className="text-red-900">{item.what_missed}</p>
                          </div>
                        )}
                        {item.what_rep_should_have_said && (
                          <div className={item.what_missed ? 'pt-2 border-t border-red-200' : ''}>
                            <p className="text-xs font-semibold text-red-700 uppercase mb-1">Should Have Said</p>
                            <p className="text-red-800">{item.what_rep_should_have_said}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {item.customer_reaction && (
                  <p className="text-sm text-gray-500 italic">
                    Customer reaction: {item.customer_reaction}
                  </p>
                )}

                <SentimentRow
                  before={item.sentiment_before}
                  after={item.sentiment_after}
                  trend={item.sentiment_trend}
                />

                {(item.stored_answer || item.ai_answer) && (
                  <div>
                    <button
                      onClick={() => toggleAnswers(idx)}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium focus:outline-none"
                      type="button"
                    >
                      {expanded.answers.has(idx) ? 'Hide Suggested Answers' : 'Show Suggested Answers'}
                      <span className="ml-1">{expanded.answers.has(idx) ? '▲' : '▼'}</span>
                    </button>
                    {expanded.answers.has(idx) && (
                      <div className="mt-2 space-y-2">
                        {item.stored_answer && (
                          <div className="bg-green-50 border border-green-200 rounded p-3 text-sm">
                            <p className="font-semibold text-green-800 mb-1">Knowledge Base Answer</p>
                            <p className="text-green-900">{item.stored_answer}</p>
                          </div>
                        )}
                        {item.ai_answer && (
                          <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
                            <p className="font-semibold text-blue-800 mb-1">AI-Generated Answer</p>
                            <p className="text-blue-900">{item.ai_answer}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <TranscriptBlock
                  transcripts={item.relevant_transcripts ?? []}
                  isExpanded={expanded.transcripts.has(idx)}
                  onToggle={() => toggleTranscripts(idx)}
                />
              </div>
            )}
          </div>
        );
      })}

      <ManagerReviewPanel
        sessionId={sessionId}
        currentSection="objection_faq"
        managerReview={managerReview}
        isAdmin={isAdmin}
        onReviewSaved={onReviewSaved}
      />
    </div>
  );
};

export default ObjFaqAdherenceSection;
